import { WorkItemStatus, WorkItemDetailStatus } from "../../utils/constants";

// Re-export so call sites can keep importing this enum alongside the rest
// of the detail-page model types without reaching into `utils/constants`.
export { WorkItemDetailStatus } from "../../utils/constants";

/**
 * Full WorkItem returned by LoadWorkItem. Only fields used by the UI are
 * declared here. `assignee` and `isShadowMode` are intentionally omitted —
 * declaring them would imply rendering them.
 */
export interface WorkItem {
  workItemId: string;
  exceptionName?: string;
  severity?: string; // HIGH | MEDIUM | LOW
  status?: string; // ACTIVE | RESOLVED | EXPIRED
  processPath?: string;
  process?: string;
  operator?: string;
  grainType?: string;
  grainId?: string;
  actionStatus?: string;
  description?: string;

  createdTime?: number; // epoch ms (Smithy Long)
  updatedTime?: number; // epoch ms
  lastWorkItemStatusUpdatedTime?: number; // epoch ms

  additionalAttributes?: Record<string, string>;
}

export interface LoadWorkItemRequest {
  workItemId: string;
}

export interface LoadWorkItemResponse {
  workItem: WorkItem;
}

export interface UpdateWorkItemStateRequest {
  workItemId: string;
  status: WorkItemStatus; // ACTIVE | RESOLVED | EXPIRED
  operator: string;
}

export interface UpdateWorkItemStateResponse {
  message?: string;
  responseStatus?: string;
}

export type AsyncStatus = WorkItemDetailStatus;

export interface WorkItemDetailState {
  /** Work item id whose data is currently loaded into `workItem` (or was last loaded). */
  loadedWorkItemId: string | null;

  /** Most recently loaded work item; null until a fulfilled load. */
  workItem: WorkItem | null;

  /** Lifecycle of the LoadWorkItem thunk. */
  loadStatus: AsyncStatus;
  loadError: string | null;

  /**
   * Lifecycle of the UpdateWorkItemState thunk driven by the Resolve button.
   * Distinct from `loadStatus` so the button's disabled state and the page's
   * post-resolve navigation are both derivable from the store.
   *
   * Reset to `IDLE` on every LoadWorkItem start so a stale `FULFILLED`
   * cannot auto-navigate when the user returns to the page for a new id.
   */
  resolveStatus: AsyncStatus;
  resolveError: string | null;
}

export const initialState: WorkItemDetailState = {
  loadedWorkItemId: null,
  workItem: null,
  loadStatus: WorkItemDetailStatus.IDLE,
  loadError: null,
  resolveStatus: WorkItemDetailStatus.IDLE,
  resolveError: null,
};
